import { DataLayer } from "@sazka/utils/data-layer";

const pushedItems = new Set<string>();

function extractCreativeFromBanner(banner: Element): string {
    const creativeElements = banner.querySelectorAll(
        ".ds-promo-banner-new__heading, .ds-promo-banner-new__description"
    );
    const creativeTexts = Array.from(creativeElements)
        .map((el) => el.textContent?.trim().replace(/\s+/g, " ") || "")
        .filter((text) => text.length > 0);
    return creativeTexts.join("; ");
}

function getHeight(banner: Element): "low" | "high" {
    return banner.classList.contains("ds-promo-banner-new--high") ? "high" : "low";
}

function setupCarouselObserver(carousel: HTMLElement, height: "low" | "high") {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName === "class") {
                const targetElement = mutation.target as HTMLElement;
                if (targetElement.classList.contains("ds-promo-card__inner--active")) {
                    const codeName = targetElement.getAttribute("data-a-codename") || "";
                    const itemIndex = Array.from(carousel.querySelectorAll(".js-ds-promo-card-item")).indexOf(
                        targetElement
                    );
                    const creative = extractCreativeFromBanner(targetElement);
                    const position = `${itemIndex + 1}`;
                    const uniqueKey = `${codeName}_${height}_${creative}_${position}`;

                    if (!pushedItems.has(uniqueKey)) {
                        DataLayer.push({
                            event: "action.impress.promos",
                            action: {
                                impress: {
                                    promos: [
                                        {
                                            id: codeName,
                                            name: `${codeName}.${height}`,
                                            creative: creative,
                                            position,
                                        },
                                    ],
                                },
                            },
                        });
                        pushedItems.add(uniqueKey);
                    }
                }
            }
        });
    });

    const config = { attributes: true, childList: false, subtree: true };
    observer.observe(carousel, config);
}

function setupBannerClickObserver(element: HTMLElement, height: "low" | "high", creative: string) {
    element.addEventListener("click", () => {
        let position = "1";
        const codeName = element.getAttribute("data-a-codename") || "";
        // If element is a carousel item, find its index to calculate position
        if (element.closest(".ds-promo-banner-new--carousel")) {
            const carousel = element.closest(".ds-promo-banner-new--carousel");
            const itemIndex = Array.from(carousel.querySelectorAll(".ds-promo-banner-new__inner")).indexOf(element);
            position = `${itemIndex + 1}`;
        }

        DataLayer.pushInstant({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: codeName,
                            name: `${codeName}.${height}`,
                            creative,
                            position,
                        },
                    ],
                },
            },
        });
    });
}

export function initializeCPWidgetBannerAnalytics() {
    const banners = document.querySelectorAll(".ds-promo-banner-new");
    banners.forEach((banner) => {
        const isCarousel = banner.classList.contains("ds-promo-banner-new--carousel");
        const codeName = banner.querySelector("[data-a-codename]")?.getAttribute("data-a-codename") || "";
        const height = getHeight(banner);
        const creative = extractCreativeFromBanner(banner);

        if (isCarousel) {
            const carouselItems = banner.querySelectorAll(".ds-promo-banner-new__inner");
            carouselItems.forEach((item, itemIndex) => {
                setupBannerClickObserver(item as HTMLElement, height, extractCreativeFromBanner(item));

                if (item.classList.contains("ds-promo-card__inner--active")) {
                    const position = (itemIndex + 1).toString();
                    const itemCreative = extractCreativeFromBanner(item);

                    // Initial impression for the active carousel item
                    const uniqueKey = `${codeName}_${height}_${itemCreative}_${position}`;
                    if (!pushedItems.has(uniqueKey)) {
                        DataLayer.push({
                            event: "action.impress.promos",
                            action: {
                                impress: {
                                    promos: [
                                        {
                                            id: codeName,
                                            name: `${codeName}.${height}`,
                                            creative: itemCreative,
                                            position,
                                        },
                                    ],
                                },
                            },
                        });
                        pushedItems.add(uniqueKey);
                    }
                }
            });

            setupCarouselObserver(banner as HTMLElement, height);
        } else {
            const bannerItem = banner.querySelector(".ds-promo-banner-new__inner");
            setupBannerClickObserver(bannerItem as HTMLElement, height, creative);

            const uniqueKey = `${codeName}_${height}_${creative}_1`;
            if (!pushedItems.has(uniqueKey)) {
                DataLayer.push({
                    event: "action.impress.promos",
                    action: {
                        impress: {
                            promos: [
                                {
                                    id: codeName,
                                    name: `${codeName}.${height}`,
                                    creative: creative,
                                    position: "1",
                                },
                            ],
                        },
                    },
                });
                pushedItems.add(uniqueKey);
            }
        }
    });
}
